import React, { useMemo } from "react";
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Icon,
  Label,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik, FormikErrors } from "formik";
import * as Yup from "yup";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../app/stores/store";
import PageHeader from "../../../app/layout/PageHeader";
import { UpsertSettingsTaxRatesFormValues } from "../../../app/models/pricing";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { taxOptions } from "../../../app/common/options/option";
import MyIconDecimalInput from "../../../app/common/form/MyIconDecimalInput";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import { SalesTaxOptionType } from "../../../app/models/enums";
import { salesTaxOptionChecked } from "../../../app/common/util/functions";

interface Props {
  isAdd: boolean;
}

export default observer(function UpsertTaxRate({ isAdd }: Props) {
  const { t } = useTranslation([
    "common",
    "translation",
    "errors",
    "settings",
    "pricingSettings",
  ]);

  const { modalStore, multipleTaxRatesStore } = useStore();
  const {
    selectedSettingsTaxRate,
    loadMultipleTaxRates,
    createSettingsTaxRate,
    updateSettingsTaxRate,
  } = multipleTaxRatesStore;
  const formValues = useMemo(
    () =>
      selectedSettingsTaxRate
        ? new UpsertSettingsTaxRatesFormValues(selectedSettingsTaxRate)
        : new UpsertSettingsTaxRatesFormValues(),
    [selectedSettingsTaxRate]
  );

  const validationSchema = Yup.object({
    name: Yup.string().required(
      t("required_tax_rate_name", {
        ns: "errors",
      })
    ),
    taxRate: Yup.string().required(
      t("required_tax_rate_number", {
        ns: "errors",
      })
    ),
  });

  function handleFormSubmit(
    values: UpsertSettingsTaxRatesFormValues,
    setErrors: (errors: FormikErrors<UpsertSettingsTaxRatesFormValues>) => void,
    setSubmitting: (isSubmitting: boolean) => void
  ) {
    const action = () =>
      isAdd
        ? createSettingsTaxRate(values.toJSON())
        : updateSettingsTaxRate(selectedSettingsTaxRate?.id!, values.toJSON());

    action()
      .then(() => {
        loadMultipleTaxRates();
      })
      .catch((error) => {
        console.log("error:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
    modalStore.closeModal();
  }

  return (
    <>
      <PageHeader
        type={"h3"}
        header={
          isAdd
            ? t("tax_rates.new", {
                ns: "settings",
              })
            : t("tax_rates.edit", {
                ns: "settings",
              })
        }
        divider={true}
        addTitle={false}
      />
      <Formik
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors, setSubmitting }) =>
          handleFormSubmit(values, setErrors, setSubmitting)
        }
        initialValues={formValues}
      >
        {({ handleSubmit, setFieldValue, values, handleChange, handleBlur, errors, touched }) => (
          <Form
            className="ui form error"
            p
            autoComplete="on"
            onSubmit={handleSubmit}
          >
            <Grid>
              <Grid.Row>
                <Grid.Column>
                  <MyTextInput
                    label={`${t("tax_rates.taxName", { ns: "settings" })}:`}
                    placeholder={`${t("tax_rates.taxName", { ns: "settings" })}`}
                    className="required"
                    name={"name"}
                    maxLength={25}
                    autoCapitalizeFirstLetter={false}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <MyIconDecimalInput
                    name="taxRate"
                    placeholder={t("tax.rate", { ns: "pricingSettings" })}
                    icon="percent"
                    label={`${t("tax.rate", { ns: "pricingSettings" })}:`}
                    className="drop-down required mb-0"
                    showHelp={false}
                    maxLength={500}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <label className="myLabel pb-3 required">{`${t("tax.apply2", {
                    ns: "pricingSettings",
                  })}:`}</label>
                  <Form.Group grouped>
                    {taxOptions.map((data, index) => {
                      //console.log(data, index);
                      return (
                        <Form.Field key={`salesTaxOption[${data.value}]`}>
                          <Checkbox
                            id={`salesTaxOption-${index}`}
                            key={`salesTaxOption-${data.value}`}
                            value={data.value}
                            name={`salesTaxOption-${data.value}`}
                            onChange={(e, { checked }) => {
                              const truthy =
                                data.value === SalesTaxOptionType.ExemptMisc
                                  ? !checked
                                  : checked;
                              const newValue = truthy
                                ? (values.salesTaxOption as SalesTaxOptionType) | (data.value as number)
                                : (values.salesTaxOption as SalesTaxOptionType) & ~data.value;

                              if (
                                data.value === SalesTaxOptionType.OnTicket &&
                                checked
                              ) {
                                // If "onticket" is checked, also check "parts" and "labor"
                                values.salesTaxOption =
                                  newValue |
                                  SalesTaxOptionType.Parts |
                                  SalesTaxOptionType.Labor;
                              } else if (
                                (data.value === SalesTaxOptionType.Parts ||
                                  data.value === SalesTaxOptionType.Labor) &&
                                !checked
                              ) {
                                // If requirements of "onticket" are unchecked, uncheck "onticket"
                                values.salesTaxOption =
                                  newValue & ~SalesTaxOptionType.OnTicket;
                              } else {
                                values.salesTaxOption = newValue;
                              }

                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            label={data.text}
                            checked={salesTaxOptionChecked(
                              (values.salesTaxOption as SalesTaxOptionType),
                              data.value as number
                            )}
                          />
                        </Form.Field>
                      );
                    })}
                  </Form.Group>
                  {touched.salesTaxOption && errors.salesTaxOption && (
                    <Label basic color="red">
                      {errors.salesTaxOption}
                    </Label>
                  )}
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <MyIconDecimalInput
                    name="maxTaxAmount"
                    placeholder={t("tax.maxRate", { ns: "pricingSettings" })}
                    icon="dollar"
                    iconPosition="left"
                    label={`${t("tax.maxRate", { ns: "pricingSettings" })}:`}
                    className="mb-0"
                    showHelp={false}
                    maxLength={500}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  {" "}
                  <MyCheckbox
                    label={`${t("tax.ignoreTaxExempt", {
                      ns: "pricingSettings",
                    })}:`}
                    name="ignoreTaxExempt"
                    disabled={false}
                    updateValue={(checked) => {
                      setFieldValue("ignoreTaxExempt", checked ?? false);
                    }}
                    helpName="settings.multipleTaxRates.ignoreTaxExempt"
                    showHelp
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Button color="green" type="submit">
                    <Icon name="check" />
                    {isAdd
                      ? t("add", { ns: "common" })
                      : t("save", { ns: "common" })}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      modalStore.closeModal();
                    }}
                  >
                    <Icon name="x" />
                    {t("close", { ns: "common" })}
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
});
